import { useCallback } from "react";
import Konva from "konva";
import { Template, ImageElement, SvgElement } from "../core/template.schemas";
import { useElementFactory } from "./useElementFactory";
import { ResolveContext, resolveImageSource } from "../core/runtime/resolve";
import { useGoogleFonts } from "../template-editor/hooks/useGoogleFonts";
import { fetchImageBlobUrl } from "./useCacheBustedImage";
import {
  EntitySlotSelectionMap,
  SelectorSelectionMap,
} from "../create-post/types/PostGenerationTypes";

/**
 * Export options for template PNG export
 */
interface ExportOptions {
  scale?: number;
  quality?: number;
  filename?: string;
  useCameraScale?: boolean;
  minScale?: number;
  download?: boolean;
  entitySlots?: EntitySlotSelectionMap;
  selectorSelections?: SelectorSelectionMap;
  overrides?: Record<string, any>;
  manualOverrides?: Record<string, Record<string, any>>;
  mimeType?: string; // 'image/png' | 'image/jpeg'
  returnType?: "blobURL" | "dataURL"; // Default: 'blobURL'
}

/**
 * Stored properties for background element restoration
 */
interface BackgroundProperties {
  shadowBlur: number;
  cornerRadius: number;
  shadowColor: string;
}

/**
 * Export state management for cleanup and restoration
 */
interface ExportState {
  stage: Konva.Stage;
  shouldCleanup: boolean;
  originalBackgroundProps: BackgroundProperties | null;
  originalClipFunctions: Map<
    Konva.Layer,
    ((ctx: CanvasRenderingContext2D) => void) | null
  >;
  transformer: Konva.Transformer | null;
  transformerWasVisible: boolean;
}

/**
 * Hook for exporting templates as PNG images
 */
export const useTemplateExport = () => {
  const { createKonvaShape, createTemplateBackground } = useElementFactory();
  const { ensureTemplateFontsLoaded } = useGoogleFonts();

  /**
   * Preloads all images and SVGs into the cache before export
   */
  const preloadAssets = async (
    template: Template,
    resolveContext: ResolveContext,
  ) => {
    const promises: Promise<string>[] = [];

    template.elements.forEach((element) => {
      if (!element.visible) return;

      if (element.type === "image") {
        const imgEl = element as ImageElement;
        const src = resolveImageSource(imgEl.source, resolveContext);
        if (src) {
          promises.push(fetchImageBlobUrl(src));
        }
      } else if (element.type === "svg") {
        const svgEl = element as SvgElement;
        const src = resolveImageSource(svgEl.source, resolveContext);
        if (src) {
          promises.push(fetchImageBlobUrl(src));
        }
      }
    });

    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
  };

  /**
   * Creates export options with defaults
   */
  const createExportOptions = (
    template: Template,
    options: ExportOptions = {},
  ): ExportOptions => ({
    scale: options.scale ?? 2,
    quality: options.quality ?? 1,
    filename: options.filename ?? `${template.title || "template"}.png`,
    useCameraScale: options.useCameraScale ?? false,
    minScale: options.minScale ?? 1,
    download: options.download ?? false,
    entitySlots: options.entitySlots,
    selectorSelections: options.selectorSelections,
    mimeType: options.mimeType || "image/png",
    returnType: options.returnType || "blobURL",
  });

  /**
   * Creates a clipping function for canvas bounds
   */
  const createCanvasClipFunction =
    (width: number, height: number) => (ctx: CanvasRenderingContext2D) => {
      ctx.rect(0, 0, width, height);
      ctx.clip();
    };

  /**
   * Applies safety wrapper to all image nodes to prevent rendering crashes
   */
  const safeguardImages = (stage: Konva.Stage) => {
    const imageNodes = stage.find("Image") as Konva.Image[];
    imageNodes.forEach((node) => {
      // Monkey-patch sceneFunc to catch draw errors
      node.sceneFunc((context, shape) => {
        try {
          // Check for broken image state before attempting draw
          const img = shape.image() as HTMLImageElement | null;
          if (img && img.complete && img.naturalWidth === 0) {
            return;
          }

          // @ts-ignore - accessing internal Konva method
          const internalSceneFunc = (Konva.Image.prototype as any)._sceneFunc;
          if (internalSceneFunc) {
            internalSceneFunc.call(shape, context);
          }
        } catch (e) {
          // Silent failure for broken images during export
          // This prevents the entire export from failing
        }
      });
    });
  };

  /**
   * Creates temporary stage for export
   */
  const createTemporaryStage = (
    template: Template,
    resolveContext: ResolveContext,
  ): ExportState => {
    // Create hidden container
    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "absolute",
      top: "-9999px",
      left: "-9999px",
    });
    document.body.appendChild(container);

    // Create stage
    const stage = new Konva.Stage({
      container,
      width: template.canvas.width,
      height: template.canvas.height,
    });

    // Create and setup layer
    const layer = new Konva.Layer();
    const clipFunction = createCanvasClipFunction(
      template.canvas.width,
      template.canvas.height,
    );
    layer.clipFunc(clipFunction);
    stage.add(layer);

    // Add background
    const background = createTemplateBackground(
      template.canvas.width,
      template.canvas.height,
      true,
    );
    layer.add(background);

    // Add template elements
    const sortedElements = [...template.elements]
      .filter((el) => el.visible)
      .sort((a, b) => a.zIndex - b.zIndex);

    sortedElements.forEach((element) => {
      const shape = createKonvaShape(element, resolveContext);
      if (shape) {
        layer.add(shape);
      }
    });

    // Apply safety guards to all images immediately
    safeguardImages(stage);

    try {
      layer.draw();
    } catch (e) {
      console.warn("Initial export draw skipped due to image state:", e);
    }

    return {
      stage,
      shouldCleanup: true,
      originalBackgroundProps: null,
      originalClipFunctions: new Map(),
      transformer: null,
      transformerWasVisible: false,
    };
  };

  /**
   * Cleans up temporary stage and container
   */
  const cleanupTemporaryStage = (stage: Konva.Stage): void => {
    const container = stage.container();
    stage.destroy();

    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  /**
   * Triggers download of the exported image
   */
  const triggerDownload = (dataURL: string, filename: string): void => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Wait until all Konva.Image nodes have loaded their underlying images.
   */
  const waitForStageAssets = async (stage: Konva.Stage): Promise<void> => {
    const imageNodes = stage.find("Image") as Konva.Image[];

    const waitForImage = (img: HTMLImageElement): Promise<void> => {
      if (img.complete) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    };

    await Promise.all(
      imageNodes.map(async (node) => {
        // Check for explicit loading promise (e.g. for SVGs fetching data)
        const loadingPromise = node.getAttr("loadingPromise");
        if (loadingPromise) {
          await loadingPromise;
        }

        const img = node.image() as HTMLImageElement | null;
        if (!img) {
          return;
        }

        // Note: useElementFactory now uses fetchImageBlobUrl internally,
        // so images should already be using blob URLs.
        // We just wait for them to load.

        if (img.complete && img.naturalWidth > 0) {
          return;
        }
        return waitForImage(img);
      }),
    );

    // Final safety check: remove any images that are still broken or are placeholders to prevent InvalidStateError
    imageNodes.forEach((node) => {
      const img = node.image() as HTMLImageElement | null;
      if (!img) return;

      const anyNode = node as any;
      const elementType =
        typeof anyNode.getAttr === "function"
          ? anyNode.getAttr("elementType")
          : anyNode.attrs?.elementType;
      const isSvgElement = elementType === "svg";

      const isBroken = img.complete && img.naturalWidth === 0;
      const src = img.src || "";
      // Check both the URL string (fallback) and the explicit attribute we added
      const isPlaceholderSrc = src.includes("/defaults/image_placeholder.png");
      const isPlaceholderAttr = node.getAttr("isPlaceholder") === true;
      const isPlaceholder = isPlaceholderSrc || isPlaceholderAttr;

      // Do not remove SVG elements even if they look "broken" – otherwise they disappear im Export.
      if ((isBroken && !isSvgElement) || isPlaceholder) {
        console.warn(
          "[Export][Assets] removing broken or placeholder image from stage before export",
          {
            id: node.id?.(),
            src,
            isBroken,
            isPlaceholder,
            isPlaceholderSrc,
            isPlaceholderAttr,
            elementType,
          },
        );
        node.image(null as any);
      }
    });
  };

  const exportAsPNG = useCallback(
    async (
      template: Template,
      options: ExportOptions = {},
    ): Promise<string> => {
      const exportOptions = createExportOptions(template, options);

      try {
        // Ensure all template fonts are loaded before creating/drawing the stage
        await ensureTemplateFontsLoaded(template);

        const selectedSlots: { [slotId: string]: any } = {};
        const selectedSelectors: { [selectorId: string]: any } = {};
        const manualSlotOverridesById: Record<string, Record<string, any>> = {};

        if (options.entitySlots) {
          Object.entries(options.entitySlots).forEach(([slotKey, slot]) => {
            if (slot.selectedInstance && slot.slotId) {
              selectedSlots[slot.slotId] = slot.selectedInstance.data;
            }

            if (
              options.manualOverrides &&
              options.manualOverrides[slotKey] &&
              slot.slotId
            ) {
              if (!manualSlotOverridesById[slot.slotId]) {
                manualSlotOverridesById[slot.slotId] = {};
              }
              Object.assign(
                manualSlotOverridesById[slot.slotId],
                options.manualOverrides[slotKey],
              );
            }
          });
        }
        if (options.selectorSelections) {
          Object.values(options.selectorSelections).forEach((sel) => {
            selectedSelectors[sel.selectorId] =
              sel.selectedValue ?? sel.defaultValue ?? undefined;
          });
        }

        const resolveContext: ResolveContext = {
          selected: { slots: selectedSlots, selectors: selectedSelectors },
          overrides: options.overrides || {},
          manualSlotOverrides: manualSlotOverridesById,
          templateSlots: template.slots || [],
        };

        // Preload assets into cache so useElementFactory can use them immediately
        await preloadAssets(template, resolveContext);

        const exportState = createTemporaryStage(template, resolveContext);

        // Wait for images
        await waitForStageAssets(exportState.stage);

        // Draw the stage and export
        exportState.stage.draw();

        let dataURL: string;

        if (exportOptions.returnType === "dataURL") {
          const canvas = exportState.stage.toCanvas({
            pixelRatio: exportOptions.scale,
          });
          dataURL = canvas.toDataURL(
            exportOptions.mimeType || "image/png",
            exportOptions.quality,
          );
        } else {
          // Use toBlob + createObjectURL instead of toDataURL to prevent memory crashes with large Base64 strings
          // especially on mobile devices.
          const blob = await new Promise<Blob | null>((resolve) => {
            // Konva's toBlob or toCanvas().toBlob is safer
            const canvas = exportState.stage.toCanvas({
              pixelRatio: exportOptions.scale,
            });

            canvas.toBlob(
              (b) => {
                resolve(b);
              },
              exportOptions.mimeType || "image/png",
              exportOptions.quality,
            );
          });

          if (!blob) {
            throw new Error("Failed to generate image blob");
          }

          dataURL = URL.createObjectURL(blob);
        }

        if (exportOptions.download) {
          triggerDownload(dataURL, exportOptions.filename || "download");
        }

        // Cleanup
        if (exportState.shouldCleanup) {
          cleanupTemporaryStage(exportState.stage);
        }

        return dataURL;
      } catch (error) {
        console.error("Failed to export template as PNG:", error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createKonvaShape, createTemplateBackground, ensureTemplateFontsLoaded],
  );

  /**
   * Generates a low-quality, small thumbnail for the template
   * @param template - The template to generate thumbnail for
   * @param overrides - Optional overrides to apply
   */
  const generateThumbnail = useCallback(
    async (
      template: Template,
      overrides?: Record<string, any>,
    ): Promise<string> => {
      const MAX_DIMENSION = 500;
      const { width, height } = template.canvas;

      // Calculate scale to fit within MAX_DIMENSION x MAX_DIMENSION
      const scale = Math.min(
        MAX_DIMENSION / width,
        MAX_DIMENSION / height,
        1, // Don't upscale if smaller
      );

      return exportAsPNG(template, {
        scale,
        quality: 0.6, // Low quality for compression
        download: false,
        overrides,
        returnType: "dataURL", // Force Base64 for backend compatibility (thumbnails are small enough)
      });
    },
    [exportAsPNG],
  );

  /**
   * Exports the current template using an existing stage reference
   * @param template - The template to export
   * @param stageRef - Reference to the Konva stage
   * @param options - Export options
   */
  const exportCurrentTemplate = useCallback(
    async (
      template: Template,
      stageRef: React.RefObject<Konva.Stage>,
      options?: ExportOptions,
    ): Promise<string> => {
      // Derive export pixelRatio from editor zoom when requested.
      const currentStageScale = stageRef.current?.scaleX() ?? 1;
      const effectiveScale = options?.useCameraScale
        ? Math.max(options?.minScale ?? 1, currentStageScale)
        : options?.scale; // keep manual override if provided

      return exportAsPNG(template, {
        // ignore stageRef for layout; use offscreen stage
        ...options,
        scale: effectiveScale,
      });
    },
    [exportAsPNG],
  );

  return {
    exportAsPNG,
    exportCurrentTemplate,
    generateThumbnail,
  };
};
